import "./MeetingContentPage.scss";

import { Accordion, Alert, ErrorIcon, Flex, Grid, Text } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { SurveyDTO, TeamInfoDTO } from "@teams/types/api";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import { EmptyResultsComponent } from "../../../_shared/components/EmptyResultsComponent";
import { NavBarNew } from "../../../_shared/components/NavBar/NavBarNew";
import { NavigationHeader } from "../../../_shared/components/NavigationHeader";
import { PollyDialog } from "../../../_shared/components/PollyDialog";
import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { TeamsLoader } from "../../../_shared/components/TeamsLoader";
import { ChannelSurveysOptions } from "../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { startAuthoringTask } from "../../../_shared/utils/startTaskWrapper";
import { useInterval } from "../../../_shared/utils/useInterval";
import useWindowDimensions from "../../../_shared/utils/useWindowDimensions";
import { MeetingEmptyState } from "./components/MeetingEmptyState";
import { SurveyDetailsPage } from "./components/SurveyDetailsPage";
import { useMeetingSurveys } from "./hooks/useMeetingSurveys";
import { SurveyCard } from "./SurveyCard";

const ACTIVE_HISTORY_HOURS = 24;

export const MeetingContentPage: React.FC<{ teamInfo: TeamInfoDTO }> = ({ teamInfo }) => {
  const { teamsContext } = useTeamsContext();
  const { user, loadUser } = useUserInfo();
  const {
    surveys,
    loadSurveys,
    isLoading,
    error: surveysError,
  } = useMeetingSurveys(teamsContext, (a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [historicalHours, setHistoricalHours] = useState<number | undefined>(ACTIVE_HISTORY_HOURS);

  const surveyLoadOptions = useMemo((): ChannelSurveysOptions => {
    return {
      closeAt: historicalHours ? moment().subtract(historicalHours, "hours").toDate() : undefined,
    };
  }, [historicalHours]);

  useEffect(() => {
    (async function () {
      await loadSurveys(surveyLoadOptions);
      setHasLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyLoadOptions]);

  const [activeSurveys, historicalSurveys] = useMemo(() => {
    const closeAtCheck = moment().subtract(ACTIVE_HISTORY_HOURS, "hours").toDate().getTime();

    const active: SurveyDTO[] = [];
    const historical: SurveyDTO[] = [];

    surveys.forEach(survey => {
      if (!survey.closeAt || survey.closeAt.getTime() > closeAtCheck) {
        active.push(survey);
      } else {
        historical.push(survey);
      }
    });

    return [
      active.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
      historical.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    ];
  }, [surveys]);

  useInterval(() => {
    if (!isLoading) {
      loadUser(true);
      loadSurveys(surveyLoadOptions);
    }
  }, 10000);

  return (
    <Flex column gap="gap.medium" className="meeting-content-page">
      <NavBarNew />

      {teamInfo.meetingRole !== "Attendee" && (
        <NavigationHeader
          actions={[
            {
              key: "create",
              primary: true,
              onClick: () => {
                startAuthoringTask(() => {
                  loadSurveys(surveyLoadOptions);
                });
              },
              content: <PollyIcon icon="create-icon" label="Create" inheritColor />,
            },
          ]}
        />
      )}

      <TeamsLoader isLoading={!hasLoaded} loadingMessage="Loading...">
        <Flex column gap="gap.small">
          {surveysError && <Alert danger content="Error loading pollys" icon={<ErrorIcon />} />}

          {!surveysError && surveys.length === 0 && (
            <>
              {user.featureFlags.Test_2021Q2MeetingTab === "new cta" ? (
                <EmptyResultsComponent />
              ) : (
                <MeetingEmptyState
                  header={
                    <Flex column gap="gap.smaller">
                      <Text
                        content="You haven't created any meeting questions yet"
                        size="large"
                        weight="bold"
                        as="div"
                        align="center"
                      />
                      <Text
                        content='Get started preparing questions for by hitting "create" above!'
                        align="center"
                      />
                    </Flex>
                  }
                />
              )}
            </>
          )}

          {!surveysError && surveys.length > 0 && (
            <>
              <SurveysSection
                surveys={activeSurveys}
                teamsContext={teamsContext}
                onSurveyChanged={() => loadSurveys(surveyLoadOptions)}
              />

              <Accordion
                expanded
                onActiveIndexChange={(_a, b) => {
                  if ((b?.activeIndex as number[]).includes(1)) {
                    setHistoricalHours(undefined);
                  } else {
                    setHistoricalHours(ACTIVE_HISTORY_HOURS);
                  }
                }}
                panels={[
                  {
                    title: (
                      <Text key="titlearchived" content="Archived" size="large" weight="bold" />
                    ),
                    content: (
                      <SurveysSection
                        key="content:archived"
                        surveys={historicalSurveys}
                        teamsContext={teamsContext}
                        onSurveyChanged={() => loadSurveys(surveyLoadOptions)}
                      />
                    ),
                  },
                ]}
              />
            </>
          )}
        </Flex>
      </TeamsLoader>
    </Flex>
  );
};

const SurveysSection: React.FC<{
  teamsContext: app.Context;
  surveys: SurveyDTO[];
  onSurveyChanged: () => void;
}> = ({ surveys, teamsContext, onSurveyChanged }) => {
  const { user } = useUserInfo();
  const { width } = useWindowDimensions();
  const [viewDetails, setViewDetails] = useState<React.ReactNode>();

  return (
    <>
      <Grid
        columns={width < 600 ? 1 : width < 1200 ? 2 : width < 1600 ? 3 : 4}
        style={{ gridGap: "1rem" }}
      >
        {surveys.map((survey, index) => {
          if (!user.featureFlags.qna && survey.type === "qna") {
            return null;
          }
          return (
            <SurveyCard
              key={survey.id}
              survey={survey}
              teamsContext={teamsContext}
              onChanged={onSurveyChanged}
              position={index + 1}
              onViewResults={(survey, results) =>
                setViewDetails(<SurveyDetailsPage survey={survey} results={results} />)
              }
            />
          );
        })}
      </Grid>

      {viewDetails && (
        <PollyDialog open header="Details" onCancel={() => setViewDetails(undefined)}>
          {viewDetails}
        </PollyDialog>
      )}
    </>
  );
};
